/**
 * Sent Documents Page
 *
 * Read-only list of all sent documents to patients (touchpoints).
 * Grouped by dispatch_id so multi-channel sends appear as a single row.
 * Full-width DataGrid with cursor-based pagination (Prev/Next).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar } from '@components/primitives/Avatar';
import { useSubjectLanguageVariants } from '@features/user';
import {
  Assignment as FormIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  Event as AppointmentIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Outbox as OutboxIcon,
  Science as LabIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRowParams,
  type GridSortModel,
} from '@mui/x-data-grid';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import type { SentDocumentSortField } from '../hooks/useSentDocumentsList';
import { useSentDocumentsList } from '../hooks/useSentDocumentsList';
import { useLazyGetDocumentProxyBlobQuery } from '../store/api';
import type { SentDocumentGroup } from '../types';

import { SentDocumentFilters } from './SentDocumentFilters';
import { FormStatusBadge, SingleChannelBadge } from './SentDocumentStatusCell';
import { DocumentDetailModal } from './DocumentDetailModal';

const SENT_DOCUMENT_TYPE_LABELS: Record<string, string> = {
  form: 'Form',
  document: 'Document',
  lab: 'Lab',
  appointment: 'Appointment',
};

const SENT_DOCUMENT_TYPE_ICONS: Record<string, React.ElementType> = {
  form: FormIcon,
  document: DocumentIcon,
  lab: LabIcon,
  appointment: AppointmentIcon,
};

const SENT_DOCUMENT_TYPE_ICON_COLORS: Record<string, string> = {
  form: 'primary.main',
  document: 'action.active',
  lab: 'info.main',
  appointment: 'text.secondary',
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Custom footer for cursor-based pagination (Prev/Next only)
 */
function CursorPaginationFooter({
  hasNextPage,
  hasPreviousPage,
  onNext,
  onPrevious,
  isFetching,
}: {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNext: () => void;
  onPrevious: () => void;
  isFetching: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        px: 2,
        py: 1,
        borderTop: 1,
        borderColor: 'divider',
        minHeight: 52,
      }}
    >
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          startIcon={<PrevIcon />}
          onClick={onPrevious}
          disabled={!hasPreviousPage || isFetching}
        >
          Previous
        </Button>
        <Button
          size="small"
          endIcon={<NextIcon />}
          onClick={onNext}
          disabled={!hasNextPage || isFetching}
        >
          Next
        </Button>
      </Stack>
    </Box>
  );
}

export const SentDocumentsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { singular: subjectLanguage, singularCap: subjectLanguageCap } =
    useSubjectLanguageVariants();
  const { document: documentMetrics } = useEventTracking();

  const [fetchDocumentBlob] = useLazyGetDocumentProxyBlobQuery();

  // Document detail modal state
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Track page view
  const hasTrackedPageView = useRef(false);

  const {
    groups,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    hasPreviousPage,
    filters,
    sortBy,
    sortOrder,
    hasActiveFilters,
    isEmpty,
    goNextPage,
    goPreviousPage,
    setPatientId,
    setCommunicationTypes,
    setChannels,
    setStatuses,
    setStartDate,
    setEndDate,
    setSortBy,
    setSortOrder,
    clearFilters,
  } = useSentDocumentsList();

  // Track page view once loaded
  useEffect(() => {
    if (!isLoading && !hasTrackedPageView.current) {
      documentMetrics.pageViewed({
        page_type: 'sent_documents',
        document_count: groups.length,
        has_filters: hasActiveFilters,
      });
      hasTrackedPageView.current = true;
    }
  }, [isLoading, groups.length, hasActiveFilters, documentMetrics]);

  // Row click → open detail modal if document exists
  const handleRowClick = useCallback(
    (params: GridRowParams<SentDocumentGroup>) => {
      const group = params.row;
      documentMetrics.rowClicked({
        document_id: group.document_id ?? undefined,
        document_type: group.communication_type,
        page_type: 'sent_documents',
        has_patient: !!group.patient_id,
      });

      if (group.document_id) {
        setSelectedDocumentId(group.document_id);
        setIsDetailModalOpen(true);
      }
    },
    [documentMetrics],
  );

  const handleViewClick = useCallback((event: React.MouseEvent, group: SentDocumentGroup) => {
    event.stopPropagation();
    if (group.document_id) {
      setSelectedDocumentId(group.document_id);
      setIsDetailModalOpen(true);
    }
  }, []);

  const handleDownloadClick = useCallback(
    async (event: React.MouseEvent, group: SentDocumentGroup) => {
      event.stopPropagation();
      if (!group.document_id) return;

      try {
        const blob = await fetchDocumentBlob({
          documentId: group.document_id,
          disposition: 'attachment',
        }).unwrap();

        documentMetrics.downloaded({
          document_id: group.document_id,
          document_type: group.communication_type,
          page_type: 'sent_documents',
        });

        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `sent-document-${group.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } catch (err) {
        console.error('Download error:', err);
        enqueueSnackbar('Failed to download document', { variant: 'error' });
      }
    },
    [fetchDocumentBlob, documentMetrics, enqueueSnackbar],
  );

  const handleCloseModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedDocumentId(null);
  }, []);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length > 0) {
        const { field, sort } = model[0];
        const apiField = field as SentDocumentSortField;
        setSortBy(apiField);
        setSortOrder(sort ?? 'desc');
        documentMetrics.sortChanged({
          sort_field: apiField,
          sort_order: sort ?? 'desc',
          page_type: 'sent_documents',
        });
      } else {
        setSortBy('created_at');
        setSortOrder('desc');
      }
    },
    [setSortBy, setSortOrder, documentMetrics],
  );

  const handleNextPage = useCallback(() => {
    goNextPage();
    documentMetrics.paginationChanged({
      new_page: 0, // cursor-based, no page number
      rows_per_page: groups.length,
      total_pages: 0,
      source: 'sent_documents_page_next',
    });
  }, [goNextPage, groups.length, documentMetrics]);

  const handlePreviousPage = useCallback(() => {
    goPreviousPage();
    documentMetrics.paginationChanged({
      new_page: 0,
      rows_per_page: groups.length,
      total_pages: 0,
      source: 'sent_documents_page_prev',
    });
  }, [goPreviousPage, groups.length, documentMetrics]);

  // Column definitions
  // Extend SentDocumentGroup with virtual column fields for DataGrid
  type RowModel = SentDocumentGroup & {
    channels_col?: string;
    actions?: string;
  };

  const columns: GridColDef<RowModel>[] = useMemo(
    () => [
      {
        field: 'patient',
        headerName: subjectLanguageCap,
        minWidth: 150,
        sortable: false,
        renderCell: params => {
          const patient = params.row.patient;
          if (!patient) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }

          const firstName = patient.first_name || '';
          const lastName = patient.last_name || '';
          const displayName =
            patient.name ||
            patient.preferred_name ||
            (firstName && lastName
              ? `${firstName} ${lastName}`
              : firstName || lastName || 'Unknown');

          return (
            <Stack direction="row" spacing={1.5} sx={{alignItems: "center",  overflow: 'hidden' }}>
              <Avatar
                id={patient.patient_id}
                firstName={firstName || undefined}
                lastName={lastName || undefined}
                sx={{ width: 32, height: 32, fontSize: '0.75rem', flexShrink: 0 }}
              />
              <Typography variant="body2" noWrap title={displayName}>
                {displayName}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'communication_type',
        headerName: 'Type',
        width: 60,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: params => {
          const type = params.value as string;
          const IconComp = SENT_DOCUMENT_TYPE_ICONS[type];
          const label = SENT_DOCUMENT_TYPE_LABELS[type] || type;
          const color = SENT_DOCUMENT_TYPE_ICON_COLORS[type] || 'text.secondary';
          if (!IconComp) {
            return (
              <Typography variant="body2" color="text.secondary">
                {type}
              </Typography>
            );
          }
          return (
            <Tooltip title={label}>
              <IconComp sx={{ color }} />
            </Tooltip>
          );
        },
      },
      {
        field: 'name',
        headerName: 'Title',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: params => (
          <Typography variant="body2" noWrap title={(params.value as string) ?? ''}>
            {(params.value as string) || '—'}
          </Typography>
        ),
      },
      {
        field: 'channels_col',
        headerName: 'Status',
        flex: 1,
        minWidth: 300,
        sortable: false,
        renderCell: params => (
          <Stack direction="row" spacing={1} sx={{alignItems: "center",  width: '100%' }}>
            <Box sx={{ minWidth: 100 }}>
              <SingleChannelBadge group={params.row} channel="sms" />
            </Box>
            <Box sx={{ minWidth: 100 }}>
              <SingleChannelBadge group={params.row} channel="email" />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormStatusBadge group={params.row} />
            </Box>
          </Stack>
        ),
      },
      {
        field: 'created_at',
        headerName: 'Sent',
        width: 170,
        sortable: true,
        valueFormatter: value => formatDate(value as string),
      },
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => {
          const hasDocument = !!params.row.document_id;
          return (
            <Stack direction="row" spacing={0.5}>
              {hasDocument && (
                <>
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={e => handleDownloadClick(e, params.row)}
                      aria-label="Download document"
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={e => handleViewClick(e, params.row)}
                      aria-label="View document details"
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
          );
        },
      },
    ],
    [subjectLanguageCap, handleDownloadClick, handleViewClick],
  );

  const sortModel: GridSortModel = useMemo(
    () => [{ field: sortBy, sort: sortOrder }],
    [sortBy, sortOrder],
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, md: 3 },
        gap: 3,
      }}
    >
      {/* Page Header */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <OutboxIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Sent Documents
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {`Track the delivery status of ${subjectLanguage} documents`}
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Box sx={{ flexShrink: 0 }}>
          <Chip label={error} color="error" variant="outlined" />
        </Box>
      )}

      {/* Filters */}
      <Box sx={{ flexShrink: 0 }}>
        <SentDocumentFilters
          filters={filters}
          onPatientIdChange={setPatientId}
          onChannelsChange={setChannels}
          onStatusesChange={setStatuses}
          onCommunicationTypesChange={setCommunicationTypes}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onClearFilters={clearFilters}
          hasActiveFilters={hasActiveFilters}
          isLoading={isLoading || isFetching}
        />
      </Box>

      {/* DataGrid — Full Width */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          '& .MuiDataGrid-root': {
            border: 'none',
            height: '100%',
          },
          '& .MuiDataGrid-row:not(.row-clickable):hover': { backgroundColor: 'transparent' },
          '& .MuiDataGrid-row.row-clickable': {
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          },
        }}
      >
        <DataGrid
          rows={groups}
          columns={columns}
          getRowId={row => row.id}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 64}
          getRowClassName={params => (params.row.document_id ? 'row-clickable' : '')}
          loading={isLoading || isFetching}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          hideFooterPagination
          slots={{
            footer: () => (
              <CursorPaginationFooter
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                onNext={handleNextPage}
                onPrevious={handlePreviousPage}
                isFetching={isFetching}
              />
            ),
            noRowsOverlay: () => (
              <Stack sx={{alignItems: "center", justifyContent: "center",  height: '100%', py: 4 }}>
                <OutboxIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography color="text.secondary">
                  {isEmpty && hasActiveFilters
                    ? 'No sent documents match your filters'
                    : 'No sent documents found'}
                </Typography>
              </Stack>
            ),
          }}
          sx={{
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              py: 1,
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
          }}
        />
      </Box>

      {/* Document Detail Modal */}
      <DocumentDetailModal
        open={isDetailModalOpen}
        documentId={selectedDocumentId}
        onClose={handleCloseModal}
      />
    </Box>
  );
};

export default SentDocumentsPage;
